/**
 * Module-scoped ring buffer that captures recent console output for
 * the feedback diagnostic snapshot.
 *
 * Idempotent install: safe to call on every HMR reload. The hook only
 * wraps the real console methods once per page lifetime.
 */

type Level = 'log' | 'info' | 'warn' | 'error' | 'debug';

export interface ConsoleBufferEntry {
	level: Level;
	message: string;
	timestamp: number;
	stack?: string;
}

const INSTALL_SYMBOL = Symbol.for('amos.consoleBuffer.installed');
const DEFAULT_MAX_ENTRIES = 100;
const MESSAGE_LEN_CAP = 500;

let buffer: ConsoleBufferEntry[] = [];
let maxEntries = DEFAULT_MAX_ENTRIES;

// Saved originals for uninstall (test teardown / SSR cleanup).
type ConsoleFn = (...args: unknown[]) => void;
const originals: Partial<Record<Level, ConsoleFn>> = {};

function pushEntry(entry: ConsoleBufferEntry): void {
	buffer.push(entry);
	while (buffer.length > maxEntries) {
		buffer.shift();
	}
}

function formatArg(arg: unknown): string {
	if (typeof arg === 'string') return arg;
	if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
	try {
		return JSON.stringify(arg);
	} catch {
		return String(arg);
	}
}

function captureStack(): string | undefined {
	try {
		// Skip the top 2 frames (this function + the wrapper)
		return new Error().stack?.split('\n').slice(3, 9).join('\n');
	} catch {
		return undefined;
	}
}

export function installConsoleBuffer(opts: { maxEntries?: number } = {}): void {
	if (opts.maxEntries !== undefined) {
		maxEntries = opts.maxEntries;
	}

	// Idempotent — survives HMR, double imports
	if ((globalThis as Record<symbol, unknown>)[INSTALL_SYMBOL]) return;
	(globalThis as Record<symbol, unknown>)[INSTALL_SYMBOL] = true;

	const levels: Level[] = ['log', 'info', 'warn', 'error', 'debug'];
	for (const level of levels) {
		const original = console[level].bind(console);
		originals[level] = original;
		(console as Record<Level, (...args: unknown[]) => void>)[level] = (...args: unknown[]) => {
			try {
				const message = args.map(formatArg).join(' ').slice(0, MESSAGE_LEN_CAP);
				pushEntry({
					level,
					message,
					timestamp: Date.now(),
					stack: level === 'error' ? captureStack() : undefined
				});
			} catch {
				// Never poison the hooked console — swallow any capture failure.
			}
			original(...args);
		};
	}
}

export const consoleBuffer = {
	/** Returns up to the last N entries (newest last). */
	getSnapshot(n: number = DEFAULT_MAX_ENTRIES): ConsoleBufferEntry[] {
		return buffer.slice(-n);
	},
	/** Clears the buffer. Does not uninstall the hook. */
	clear(): void {
		buffer = [];
	},
	/**
	 * Test-only — restores the original console methods and removes the
	 * install guard so installConsoleBuffer() can be called again cleanly.
	 * Never call this in production code.
	 */
	uninstall(): void {
		const levels: Level[] = ['log', 'info', 'warn', 'error', 'debug'];
		for (const level of levels) {
			const orig = originals[level];
			if (orig) {
				(console as Record<Level, ConsoleFn>)[level] = orig;
				delete originals[level];
			}
		}
		delete (globalThis as Record<symbol, unknown>)[INSTALL_SYMBOL];
		buffer = [];
		maxEntries = DEFAULT_MAX_ENTRIES;
	}
};
